/**
 * Admin "All users" request history for the payload preview (DataPopup).
 * Fetches the last persisted main generation records of one workflow across all
 * organizations (GET /api/admin/history/recent) and maps each stored `raw_input`
 * into the same SentRequest shape the local History tab renders.
 */
import type { CategorizedUrls, SentRequest } from './requestHistory';

/** One item of `GET /api/admin/history/recent` (server-side whitelist). */
export interface AdminHistoryRow {
  id: number;
  created_at: string;
  status: string | null;
  organization_name: string | null;
  email: string | null;
  raw_input: string;
}

export interface AdminSentRequest extends SentRequest {
  generationId: number;
  organizationName: string | null;
  email: string | null;
  status: string | null;
}

export interface AdminHistoryWorkflow {
  id: number;
  title: string;
}

type GetToken = (options?: { template?: string }) => Promise<string | null>;

const URL_BUCKETS = ['image_urls', 'video_urls', 'audio_urls'] as const;

const isPlainObject = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function parseRawInput(rawInput: unknown): Record<string, any> {
  if (typeof rawInput !== 'string') return {};
  try {
    const parsed = JSON.parse(rawInput);
    return isPlainObject(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

/** D1 `datetime('now')` is UTC without a zone ('YYYY-MM-DD HH:MM:SS'); invalid → 0. */
function parseCreatedAt(createdAt: unknown): number {
  if (typeof createdAt !== 'string') return 0;
  const iso = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(createdAt)
    ? `${createdAt.replace(' ', 'T')}Z`
    : createdAt;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? 0 : ms;
}

export function generationRowToSentRequest(
  row: AdminHistoryRow,
  workflow: AdminHistoryWorkflow,
): AdminSentRequest {
  const inputs = parseRawInput(row.raw_input);
  const categorizedUrls: CategorizedUrls = { image_urls: [], video_urls: [], audio_urls: [] };

  for (const bucket of URL_BUCKETS) {
    const value = inputs[bucket];
    if (Array.isArray(value)) {
      categorizedUrls[bucket] = value.filter((url): url is string => typeof url === 'string');
      delete inputs[bucket];
    }
  }

  return {
    timestamp: parseCreatedAt(row.created_at),
    workflowId: workflow.id,
    workflowTitle: workflow.title,
    inputs,
    categorizedUrls,
    generationId: row.id,
    organizationName: row.organization_name ?? null,
    email: row.email ?? null,
    status: row.status ?? null,
  };
}

export function buildAdminHistoryLoader(
  getToken: GetToken,
  workflow: AdminHistoryWorkflow,
): () => Promise<AdminSentRequest[]> {
  return async () => {
    const token = await getToken({ template: 'withmail' });
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(
      `/api/admin/history/recent?workflowId=${encodeURIComponent(String(workflow.id))}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!response.ok) {
      throw new Error(`Failed to load admin request history (HTTP ${response.status})`);
    }

    const data = await response.json();
    const items: AdminHistoryRow[] = Array.isArray(data?.items) ? data.items : [];
    return items.map((row) => generationRowToSentRequest(row, workflow));
  };
}

/**
 * Gate for the payload preview's "All users" tab: only global admins
 * (`userAccess.isAdmin`) with a selected workflow get a loader.
 */
export function resolveAdminHistoryWorkflow(
  isAdmin: boolean | undefined,
  selectedWorkflow: { workflow_id?: number | null; workflow?: { title?: string | null } | null } | null | undefined,
): AdminHistoryWorkflow | null {
  const workflowId = selectedWorkflow?.workflow_id;
  if (!isAdmin || typeof workflowId !== 'number' || !Number.isInteger(workflowId) || workflowId <= 0) {
    return null;
  }
  return { id: workflowId, title: selectedWorkflow?.workflow?.title || '' };
}
